import React, { useState, useEffect } from 'react';
import {
    Box, Grid, 
    Typography,
    IconButton,
    Button,
    Chip,
    Avatar,
    Divider,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    useTheme,
    useMediaQuery} from '@mui/material';
import {
    Refresh as RefreshIcon,
    CheckCircle as CheckCircleIcon,
    HourglassEmpty as HourglassEmptyIcon,
    AccessTime as AccessTimeIcon,
    Edit as EditIcon,
    Replay as ReplayIcon
} from '@mui/icons-material';
import TicketDetailModal from './TicketDetailModal';
import StatusUpdateModal from './StatusUpdateModal';
import { TicketTrackingActions, GetAllSchoolList } from '../../api/auth/authService';
import { useSnackbar } from "../../components/SnackBar/SnackbarContext";

const categories = [
    'All Categories',
    'Technical Support',
    'Feedback',
    'Error-Bug Report',
    'Others'
];

const priorities = [
    'All Priorities',
    'High',
    'Medium',
    'Low'
];

const statuses = [
    'All Status',
    'Open',
    'In Progress',
    'Resolved',
    'Closed',
    'Reopened'
];
const FilterSection = ({ filters, setFilters, loadingSchools, schools }) => (
    <Box sx={{ background: "#fff", borderRadius: 2, pt: 2, px: 2, pb: 2, mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" display="flex" alignItems="center" gap={1}>
                All Tickets
            </Typography>
            <Button startIcon={<RefreshIcon />} onClick={() => setFilters({
                school: 'All Schools',
                category: 'All Categories',
                priority: 'All Priorities',
                status: 'All Status',
                assignedTo: 'All Agents',
                search: ''
            })}>
                Clear Filters
            </Button>
        </Box>

        <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <FormControl fullWidth size="small">
                    <InputLabel>School</InputLabel>
                    <Select value={filters.school} label="School" onChange={(e) => setFilters({ ...filters, school: e.target.value })} disabled={loadingSchools}>
                        {loadingSchools ? (
                            <MenuItem disabled>Loading schools...</MenuItem>
                        ) : (
                            schools.map((school) => (
                                <MenuItem key={school} value={school}>{school}</MenuItem>
                            ))
                        )}
                    </Select>
                </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <FormControl fullWidth size="small">
                    <InputLabel>Category</InputLabel>
                    <Select value={filters.category} label="Category" onChange={(e) => setFilters({ ...filters, category: e.target.value })}>
                        {categories.map((category) => (
                            <MenuItem key={category} value={category}>{category}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <FormControl fullWidth size="small">
                    <InputLabel>Priority</InputLabel>
                    <Select value={filters.priority} label="Priority" onChange={(e) => setFilters({ ...filters, priority: e.target.value })}>
                        {priorities.map((priority) => (
                            <MenuItem key={priority} value={priority}>{priority}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select value={filters.status} label="Status" onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
                        {statuses.map((status) => (
                            <MenuItem key={status} value={status}>{status}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                    fullWidth
                    placeholder="Search..."
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    size="small"
                />
            </Grid>
        </Grid>
    </Box>
);


const Helpdesk = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [openTicketDetailDialog, setOpenTicketDetailDialog] = useState(false);
    const [openStatusDialog, setOpenStatusDialog] = useState(false);
    const { showSnackbar } = useSnackbar();
    const [tickets, setTickets] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [statusCounts, setStatusCounts] = useState(null);
    const [loading, setLoading] = useState(true);
    const [schools, setSchools] = useState(['All Schools']);
    const [loadingSchools, setLoadingSchools] = useState(true);
    const [filters, setFilters] = useState({
        school: 'All Schools',
        category: 'All Categories',
        priority: 'All Priorities',
        status: 'All Status',
        assignedTo: 'All Agents',
        search: ''
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [loadingDetail, setLoadingDetail] = useState(false);

    const fetchSchools = async () => {
        setLoadingSchools(true);
        try {
            const response = await GetAllSchoolList();

            if (response.success && response.data) {
                const schoolNames = response.data.map(item => item.schoolName);
                setSchools(['All Schools', ...schoolNames]);
            }
        } catch (err) {
            return;
        } finally {
            setLoadingSchools(false);
        }
    };
    const fetchTicketDetails = async (tickeId, openForUpdate = false) => {
        setLoadingDetail(true);
        try {
            const formData = new FormData();
            formData.append('Action', 'GETBY_TicketID');
            formData.append('TickeId', tickeId);

            const response = await TicketTrackingActions(formData);
            if (response.success && response.data && response.data.length > 0) {
                const ticketData = response.data[0];
                const detailedTicket = {
                    id: ticketData.ticketNo,
                    tickeId: ticketData.tickeId,
                    subject: ticketData.title,
                    description: ticketData.description || 'No description',
                    school: ticketData.schoolName,
                    schoolId:ticketData.schoolID,
                    category: ticketData.type || 'Others',
                    priority: ticketData.priority
                        ? ticketData.priority.charAt(0).toUpperCase() + ticketData.priority.slice(1).toLowerCase()
                        : 'Low',
                    status: ticketData.statusList && ticketData.statusList.length > 0
                        ? (ticketData.statusList[0].ticketStatus || 'open').toLowerCase()
                        : (ticketData.ticketStatus || 'open').toLowerCase(),
                    createdDate: ticketData.modifiedAt,
                    assignedTo: ticketData.modifiedBy ? 'Assigned' : 'Unassigned',
                    attachments: ticketData.file ? [ticketData.file] : [],
                    statusHistory: ticketData.statusList?.map((statusItem, idx) => ({
                        id: idx + 1,
                        action: (statusItem.ticketStatus || 'Unknown')
                            .charAt(0).toUpperCase() + (statusItem.ticketStatus || 'unknown').slice(1),
                        comment: statusItem.remarks || 'No remarks',
                        timestamp: statusItem.createdAt,
                        status: (statusItem.ticketStatus || 'open').toLowerCase()
                    })) || []
                };

                setSelectedTicket(detailedTicket);
                if (!openForUpdate) {
                    setOpenTicketDetailDialog(true);
                }
            } else {
                showSnackbar('Ticket details not found', 'warning');
            }
        } catch (err) {
            console.error(err);
            showSnackbar('Failed to load ticket details', 'error');
        } finally {
            setLoadingDetail(false);
        }
    };
    const fetchTickets = async (pageNum = 1, pageSize = 10) => {
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('Action', 'GETALL_Ticket');
            formData.append('PageNumber', pageNum);
            formData.append('PageSize', pageSize);

            const response = await TicketTrackingActions(formData);

            if (response.success && response.data) {
                const apiTickets = response.data;

                const mappedTickets = apiTickets.map(item => ({
                    id: item.ticketNo,
                    tickeId: item.tickeId,
                    subject: item.title,
                    description: item.description || 'No description',
                    school: item.schoolName,
                    category: item.type || 'Others',
                    priority: item.priority ?
                        item.priority.charAt(0).toUpperCase() + item.priority.slice(1).toLowerCase() : 'Low',
                    status: item.ticketStatus?.toLowerCase() || 'open',
                    createdDate: item.modifiedAt,
                    assignedTo: item.modifiedBy ? 'Assigned' : 'Unassigned',
                    attachments: item.file ? [item.file] : []
                }));

                setTickets(mappedTickets);
                setTotalCount(apiTickets[0]?.totalCount || 0);

                if (apiTickets.length > 0 && apiTickets[0].statusCountList?.[0]) {
                    setStatusCounts(apiTickets[0].statusCountList[0]);
                }
            }
        } catch (err) {
            console.error(err);
            showSnackbar('Failed to load tickets', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTickets(1, rowsPerPage);
        fetchSchools();
    }, []);

    const statsData = statusCounts
        ? [
            {
                label: 'Open Tickets',
                value: statusCounts.openCount,
                icon: <HourglassEmptyIcon />,
                color: 'warning',
                trend: statusCounts.newlyOpenCount > 0 ? `+${statusCounts.newlyOpenCount}` : '',
            },
            {
                label: 'In Progress',
                value: statusCounts.inProgressCount,
                icon: <AccessTimeIcon />,
                color: 'info',
                trend: statusCounts.newlyInProgressCount > 0 ? `+${statusCounts.newlyInProgressCount}` : '',
            },
            {
                label: 'Resolved Today',
                value: statusCounts.resolvedCount,
                icon: <CheckCircleIcon />,
                color: 'success',
                trend: statusCounts.newlyResolvedCount > 0 ? `+${statusCounts.newlyResolvedCount}` : '',
            },
            {
                label: 'Reopened Tickets',
                value: statusCounts.reopenedCount,
                icon: <ReplayIcon />,
                color: 'error',
                trend: statusCounts.newlyReopenedCount > 0 ? `+${statusCounts.newlyReopenedCount}` : '',
            },
        ]
        : [];

    const filteredTickets = tickets.filter(ticket => {
        if (filters.search) {
            const searchTerm = filters.search.toLowerCase();
            const searchableFields = [
                ticket.id,
                ticket.subject,
                ticket.description,
                ticket.school,
                ticket.category,
                ticket.priority,
                ticket.status,
                ticket.assignedTo,
                ticket.createdBy,
                ticket.tags?.join(' ')
            ].filter(Boolean).join(' ').toLowerCase();

            if (!searchableFields.includes(searchTerm)) return false;
        }

        if (filters.school !== 'All Schools' && ticket.school !== filters.school) return false;
        if (filters.category !== 'All Categories' && ticket.category !== filters.category) return false;
        if (filters.priority !== 'All Priorities' && ticket.priority !== filters.priority) return false;
        if (filters.status !== 'All Status') {
            const statusMap = { 'In Progress': 'progress' };
            const ticketStatus = statusMap[ticket.status] || ticket.status;
            const filterStatus = statusMap[filters.status] || filters.status.toLowerCase();
            if (ticketStatus !== filterStatus) return false;
        }
        if (filters.assignedTo !== 'All Agents' && ticket.assignedTo !== filters.assignedTo) return false;
        return true;
    });

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'open': return 'warning';
            case 'progress': return 'info';
            case 'resolved': return 'success';
            case 'closed': return 'default';
            case 'reopened': return 'error';
            default: return 'default';
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'High': return 'error';
            case 'Medium': return 'warning';
            case 'Low': return 'info';
            default: return 'default';
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleTicketIdClick = (ticket, e) => {
        e.stopPropagation();
        fetchTicketDetails(ticket.tickeId, false);
    };
    const handleRefresh = () => {
        fetchTickets(page + 1, rowsPerPage);
        showSnackbar('Data refreshed successfully', 'info');
    };


    const handleChangePage = (event, newPage) => {
        setPage(newPage);
        fetchTickets(newPage + 1, rowsPerPage);
    };

    const handleChangeRowsPerPage = (event) => {
        const newSize = parseInt(event.target.value, 10);
        setRowsPerPage(newSize);
        setPage(0);
        fetchTickets(1, newSize);
    };

    const handleOpenStatusDialog = (ticket, e) => {
        e.stopPropagation();
        fetchTicketDetails(ticket.tickeId, true);
        setOpenStatusDialog(true);
    };

    const StatCard = ({ label, value, icon, color = "primary", trend }) => (
        <Box sx={{ height: "100%", borderRadius: 2, border: "1px solid", borderColor: "divider", backgroundColor: "background.paper" }}>
            <Box sx={{ p: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="body2" color="text.grey" fontWeight={600}>
                            {label}
                        </Typography>
                        <Box display="flex" alignItems="center" mt={0.5}>
                            <Typography variant="h5" fontWeight={600}>
                                {value}
                            </Typography>
                            {trend && (
                                <Typography variant="caption" sx={{ ml: 1, fontWeight: 500, color: trend.startsWith("+") ? "success.main" : "error.main" }}>
                                    {trend}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    <Avatar sx={{ width: 40, height: 40, bgcolor: `${color}.light`, color: "#fff" }}>
                        {icon}
                    </Avatar>
                </Box>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ mb: 2, background: "#fff", borderRadius: 2, p: 1 }}>
                <Box sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 2,
                    mb: 2,
                    borderRadius: "8px",
                    background: "linear-gradient(135deg, #f5f9ff, #eef3ff)",
                    border: "1px solid #e0e7ff",
                }}>
                    <Box display="flex" alignItems="center" gap={2}>
                        <Box>
                            <Typography variant="h5" fontWeight={700} sx={{ color: "#1e3a8a", lineHeight: 1.2 }}>
                                Support Helpdesk
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#64748b" }}>
                                Manage support tickets and activities
                            </Typography>
                        </Box>
                    </Box>
                    <Button variant="contained" startIcon={<RefreshIcon />} onClick={handleRefresh}
                        sx={{ boxShadow: "none", '&:hover': { boxShadow: "0 4px 12px rgba(99, 102, 241, 0.2)" } }}>
                        Refresh
                    </Button>
                </Box>
                <Grid container spacing={2}>
                    {statsData.map((stat, index) => (
                        <Grid size={{ xs: 6, sm: 3, md: 3 }} key={index}>
                            <StatCard {...stat} />
                        </Grid>
                    ))}
                </Grid>
            </Box>

            <Box sx={{ background: "#fff", borderRadius: 2, mb: 2,width: '100%', overflow: 'hidden' }}>
                <FilterSection
                    filters={filters}
                    setFilters={setFilters}
                    schools={schools}
                    loadingSchools={loadingSchools}
                />
                <Divider />
                <TableContainer sx={{ maxHeight: 440 }}>
                    <Table stickyHeader aria-label="sticky table">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell sx={{ padding: '8px', fontSize: '13px' }}>S.No</TableCell>
                                <TableCell sx={{ padding: '8px', fontSize: '13px',minWidth:170 }}>Ticket ID</TableCell>
                                <TableCell sx={{ padding: '8px', fontSize: '13px',minWidth:170 }}>Title</TableCell>
                                <TableCell sx={{ padding: '8px', fontSize: '13px',minWidth:170 }}>School Name</TableCell>
                                <TableCell sx={{ padding: '8px', fontSize: '13px',minWidth:170 }}>Status</TableCell>
                                <TableCell sx={{ padding: '8px', fontSize: '13px',minWidth:170 }}>Priority</TableCell>
                                <TableCell sx={{ padding: '8px', fontSize: '13px',minWidth:170 }}>Category Type</TableCell>
                                <TableCell sx={{ padding: '8px', fontSize: '13px',minWidth:170 }}>Date</TableCell>
                                <TableCell sx={{ padding: '8px', fontSize: '13px',minWidth:170 }}>Description</TableCell>
                                <TableCell sx={{ padding: '8px', fontSize: '13px',minWidth:120 }}>Attach File</TableCell>
                                <TableCell sx={{ padding: '8px', fontSize: '13px' }}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                                        <Typography>Loading tickets...</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : filteredTickets.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={11} align="center" sx={{ py: 8 }}>
                                        <Typography variant="h6" color="text.secondary">
                                            No tickets found
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" mt={1}>
                                            Try adjusting your filters or search keywords.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredTickets.map((ticket, index) => (
                                    <TableRow key={ticket.id} hover>
                                        <TableCell sx={{ padding: '8px', fontSize: '13px' }}>{page * rowsPerPage + index + 1}</TableCell>
                                        <TableCell sx={{ padding: '8px', fontSize: '13px' }}>
                                            <Typography variant="body2" fontWeight="medium" onClick={(e) => handleTicketIdClick(ticket, e)}
                                                sx={{ color: 'primary.main', textDecoration: 'underline', cursor: 'pointer', '&:hover': { color: 'primary.dark' }, fontSize: '13px' }}>
                                                {ticket.id}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ padding: '8px', fontSize: '13px' }}>
                                            <Typography variant="body2" fontWeight="medium" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '13px' }}>
                                                {ticket.subject}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ padding: '8px', fontSize: '13px' }}>{ticket.school}</TableCell>
                                        <TableCell sx={{ padding: '8px', fontSize: '13px' }}>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Chip sx={{lineHeight:'inherit'}} label={ticket.status.charAt(0).toUpperCase() + ticket.status.slice(1)}
                                                    color={getStatusColor(ticket.status)} size="small" variant="outlined" />
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ padding: '8px', fontSize: '13px' }}>
                                            <Chip sx={{lineHeight:'inherit'}} label={ticket.priority} color={getPriorityColor(ticket.priority)} size="small" variant="outlined" />
                                        </TableCell>
                                        <TableCell sx={{ padding: '8px', fontSize: '13px' }}>{ticket.category}</TableCell>
                                        <TableCell sx={{ padding: '8px', fontSize: '13px' }}>
                                            {ticket.createdDate ? new Date(ticket.createdDate).toLocaleDateString() : 'N/A'}
                                        </TableCell>
                                        <TableCell sx={{ padding: '8px', fontSize: '13px' }}>
                                            <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '13px' }}>
                                                {ticket.description}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ padding: '8px', fontSize: '13px' }}>
                                            {ticket.attachments && ticket.attachments.length > 0 ? (
                                                <Box display="flex" alignItems="center" gap={0.5}>
                                                    {/* <AttachmentIcon fontSize="small" color="action" /> */}
                                                    <Typography variant="body2" sx={{ fontSize: '13px' }}>
                                                        {ticket.attachments.length} file(s)
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>No files</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ padding: '8px', fontSize: '13px' }}>
                                            <Box display="flex" gap={1}>
                                                <Tooltip title="Update Ticket">
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => handleOpenStatusDialog(ticket, e)}
                                                        disabled={loadingDetail}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[10, 20, 50, 100]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Box>

            <TicketDetailModal
                open={openTicketDetailDialog}
                onClose={() => setOpenTicketDetailDialog(false)}
                selectedTicket={selectedTicket}
                isMobile={isMobile}
                formatDate={formatDate}
                getStatusColor={getStatusColor}
                getPriorityColor={getPriorityColor}
            />

            <StatusUpdateModal
                open={openStatusDialog}
                onClose={() => setOpenStatusDialog(false)}
                onStatusUpdated={() => fetchTickets(page + 1, rowsPerPage)}
                selectedTicket={selectedTicket}
                tickets={tickets}
                setTickets={setTickets}
                setSelectedTicket={setSelectedTicket}
                getStatusColor={getStatusColor}
                getPriorityColor={getPriorityColor}
            />
        </Box>
    );
};

export default Helpdesk;